import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import {
  api,
  type Balanza,
  type Cliente,
  type CuentaDeCatalogo,
  ErrorDeApi,
  type Poliza,
} from '../servicios/api'
import { telon } from '../ganchos/telon'
import { useAvisos } from '../componentes/Toast'
import { fechaContableLarga } from '../fechas'

const dinero = (n: number) =>
  n.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

// El plazo de RN-08 es DateOnly: pintarlo con `new Date(iso)` lo corría un día y el
// tablero mostraba otro. Ver `src/fechas.ts`.
const fecha = fechaContableLarga

/**
 * Balanza ajustada de cierre de ejercicio (periodo 13): captura de los ajustes de cierre
 * (depreciación, provisiones, reclasificaciones) y la balanza resultante — diciembre más esos
 * ajustes. SRS: RF-802, RF-806 · RN-01, RN-08. Cédula: G-04, G-09.
 */
export function BalanzaAjustada() {
  const { id: clienteId } = useParams<{ id: string }>()
  const [anioDelEjercicio, setAnioDelEjercicio] = useState(new Date().getFullYear() - 1)
  const [cliente, setCliente] = useState<Cliente>()
  const [balanza, setBalanza] = useState<Balanza>()
  const [ajustes, setAjustes] = useState<Poliza[]>()
  const [limite, setLimite] = useState<string>()
  const [error, setError] = useState('')
  const avisar = useAvisos()
  const [capturando, setCapturando] = useState(false)
  const [cancelando, setCancelando] = useState<Poliza | null>(null)

  async function recargar() {
    if (!clienteId) return
    try {
      const datosCliente = await api.obtenerCliente(clienteId)
      setCliente(datosCliente)
      const [datosBalanza, datosAjustes, datosLimite] = await Promise.all([
        api.balanzaAjustada(clienteId, anioDelEjercicio),
        api.listarAjustesDeCierre(clienteId, anioDelEjercicio),
        api.limiteBalanzaAjustada(clienteId, anioDelEjercicio, datosCliente.tipoPersona),
      ])
      setBalanza(datosBalanza)
      setAjustes(datosAjustes)
      setLimite(datosLimite.fechaLimite)
      setError('')
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar la balanza ajustada.')
    }
  }

  useEffect(() => {
    void recargar()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clienteId, anioDelEjercicio])

  if (error && !balanza) return <p className="error">{error}</p>
  if (!clienteId || !cliente || !balanza || !ajustes) return <p>Cargando…</p>

  return (
    <>
      <p className="sutil"><Link to={`/clientes/${clienteId}`}>← {cliente.razonSocial}</Link></p>
      <header className="encabezado">
        <h1>Balanza ajustada</h1>
        <span>
          <label style={{ margin: 0 }}>
            Ejercicio
            <select className="buscador" style={{ width: 'auto', margin: '0 0 0 6px' }} value={anioDelEjercicio}
              onChange={(e) => setAnioDelEjercicio(Number(e.target.value))}>
              {[anioDelEjercicio - 1, anioDelEjercicio, anioDelEjercicio + 1].filter((a, i, xs) => xs.indexOf(a) === i).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </label>{' '}
          <button className="primario" onClick={() => setCapturando(true)}>Capturar ajuste</button>
        </span>
      </header>

      <p className="sutil">
        Cierre del ejercicio {anioDelEjercicio}
        {limite && <> · vence el <strong>{fecha(limite)}</strong> (RN-08)</>}
        {' · '}{ajustes.length} {ajustes.length === 1 ? 'ajuste capturado' : 'ajustes capturados'}
      </p>

      <p className="nota">
        Diciembre más los ajustes del periodo 13 (depreciación, provisiones, reclasificaciones de
        cierre). Los ajustes no pertenecen a ningún mes calendario: no cambian la balanza mensual
        de diciembre, solo la balanza ajustada del ejercicio (RF-802).
      </p>

      {error && <p className="error">{error}</p>}

      {!balanza.cuadrada && (
        <p className="error">
          <strong>La balanza ajustada no cuadra:</strong> cargos {dinero(balanza.totalCargos)} contra
          abonos {dinero(balanza.totalAbonos)}. Esto no debería ocurrir con partida doble (RN-01); revisa los ajustes capturados.
        </p>
      )}

      <table className="tabla">
        <thead>
          <tr>
            <th>Cuenta</th>
            <th style={{ textAlign: 'right' }}>Saldo inicial</th>
            <th style={{ textAlign: 'right' }}>Cargos</th>
            <th style={{ textAlign: 'right' }}>Abonos</th>
            <th style={{ textAlign: 'right' }}>Saldo final ajustado</th>
          </tr>
        </thead>
        <tbody>
          {balanza.renglones.map((r) => (
            <tr key={r.cuentaId} style={{ fontWeight: r.esDeRegistro ? 400 : 700 }}>
              <td style={{ paddingLeft: 8 + r.nivel * 14 }}>
                <span className="mono">{r.numero}</span> {r.nombre}
              </td>
              <td className="mono" style={{ textAlign: 'right' }}>{dinero(r.saldoInicial)}</td>
              <td className="mono" style={{ textAlign: 'right' }}>{dinero(r.cargos)}</td>
              <td className="mono" style={{ textAlign: 'right' }}>{dinero(r.abonos)}</td>
              <td className="mono" style={{ textAlign: 'right' }}>{dinero(r.saldoFinal)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ fontWeight: 700 }}>Sumas</td>
            <td></td>
            <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(balanza.totalCargos)}</td>
            <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(balanza.totalAbonos)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>

      <h2>Ajustes capturados</h2>
      {ajustes.length === 0 ? (
        <p className="vacio">Sin ajustes de cierre para este ejercicio todavía. La balanza ajustada coincide con la de diciembre.</p>
      ) : (
        <table className="tabla">
          <thead>
            <tr><th>Folio</th><th>Concepto</th><th style={{ textAlign: 'right' }}>Importe</th><th>Estado</th><th></th></tr>
          </thead>
          <tbody>
            {ajustes.map((a) => (
              <tr key={a.id} style={{ opacity: a.estado === 'Cancelada' ? 0.5 : 1 }}>
                <td className="mono">{a.folioVisible}</td>
                <td>{a.concepto}</td>
                <td className="mono" style={{ textAlign: 'right' }}>{dinero(a.totalCargos)}</td>
                <td>
                  <span className={`insignia ${a.estado === 'Cancelada' ? 'vencido' : 'vigente'}`}>
                    {a.estado === 'Cancelada' ? 'Cancelado' : 'Registrado'}
                  </span>
                </td>
                <td>
                  {a.estado === 'Vigente' && (
                    <button className="enlace" onClick={() => setCancelando(a)}>Cancelar…</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {capturando && (
        <CapturaDeAjuste
          clienteId={clienteId}
          anioDelEjercicio={anioDelEjercicio}
          alCerrar={(mensaje) => {
            setCapturando(false)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}

      {cancelando && (
        <CancelacionDeAjuste
          clienteId={clienteId}
          anioDelEjercicio={anioDelEjercicio}
          ajuste={cancelando}
          alCerrar={(mensaje) => {
            setCancelando(null)
            if (mensaje) avisar(mensaje)
            void recargar()
          }}
        />
      )}
    </>
  )
}

interface RenglonDeAjuste {
  cuentaId: string
  cargo: string
  abono: string
  descripcion: string
}

const RENGLON_VACIO: RenglonDeAjuste = { cuentaId: '', cargo: '', abono: '', descripcion: '' }

/** Captura de un ajuste de cierre con partida doble en vivo (RN-01), fechado el 31 de diciembre del ejercicio. */
function CapturaDeAjuste({
  clienteId,
  anioDelEjercicio,
  alCerrar,
}: {
  clienteId: string
  anioDelEjercicio: number
  alCerrar: (aviso?: string) => void
}) {
  const [cuentas, setCuentas] = useState<CuentaDeCatalogo[]>([])
  const [concepto, setConcepto] = useState('')
  const [renglones, setRenglones] = useState<RenglonDeAjuste[]>([{ ...RENGLON_VACIO }, { ...RENGLON_VACIO }])
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    api.catalogoDeCuentas(clienteId)
      .then((todas) => setCuentas(todas.filter((c) => c.esDeRegistro && c.estado === 'Activa')))
      .catch(() => setCuentas([]))
  }, [clienteId])

  const totalCargos = renglones.reduce((s, r) => s + (Number(r.cargo) || 0), 0)
  const totalAbonos = renglones.reduce((s, r) => s + (Number(r.abono) || 0), 0)
  const diferencia = Math.round((totalCargos - totalAbonos) * 100) / 100
  const descuadrado = diferencia !== 0 || totalCargos === 0

  function actualizar(indice: number, cambio: Partial<RenglonDeAjuste>) {
    setRenglones((r) => r.map((renglon, i) => (i === indice ? { ...renglon, ...cambio } : renglon)))
  }

  async function registrar() {
    setError('')
    setEnviando(true)
    try {
      const poliza = await api.registrarAjusteDeCierre(clienteId, anioDelEjercicio, {
        concepto,
        movimientos: renglones
          .filter((r) => r.cuentaId && (Number(r.cargo) > 0 || Number(r.abono) > 0))
          .map((r) => ({
            cuentaId: r.cuentaId,
            cargo: Number(r.cargo) || 0,
            abono: Number(r.abono) || 0,
            descripcion: r.descripcion.trim() || undefined,
          })),
      })
      alCerrar(`Ajuste ${poliza.folioVisible} registrado: cargos = abonos = ${dinero(poliza.totalCargos)}.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo registrar el ajuste.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <div className="panel" onClick={(e) => e.stopPropagation()} style={{ width: 'min(640px, 94vw)' }}>
        <h2>Capturar ajuste de cierre · ejercicio {anioDelEjercicio}</h2>
        <p className="sutil">Se fecha el 31 de diciembre de {anioDelEjercicio} y exige partida doble (RN-01), igual que cualquier póliza.</p>

        <label>
          Concepto
          <input value={concepto} onChange={(e) => setConcepto(e.target.value)} required maxLength={500}
            placeholder="Depreciación acumulada del ejercicio" />
        </label>

        <table className="tabla">
          <thead>
            <tr>
              <th>Cuenta</th><th style={{ width: 110 }}>Debe</th><th style={{ width: 110 }}>Haber</th>
              <th>Descripción</th><th style={{ width: 30 }}></th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((renglon, i) => (
              <tr key={i}>
                <td>
                  <select value={renglon.cuentaId} onChange={(e) => actualizar(i, { cuentaId: e.target.value })}
                    style={{ width: '100%', padding: '6px 8px', fontFamily: 'inherit', fontSize: 13 }}>
                    <option value="">— cuenta de registro —</option>
                    {cuentas.map((c) => (
                      <option key={c.id} value={c.id}>{c.numero} · {c.nombre}</option>
                    ))}
                  </select>
                </td>
                <td><input inputMode="decimal" placeholder="0.00" value={renglon.cargo}
                  onChange={(e) => actualizar(i, { cargo: e.target.value, abono: e.target.value ? '' : renglon.abono })}
                  style={{ width: '100%', padding: '6px 8px', textAlign: 'right' }} /></td>
                <td><input inputMode="decimal" placeholder="0.00" value={renglon.abono}
                  onChange={(e) => actualizar(i, { abono: e.target.value, cargo: e.target.value ? '' : renglon.cargo })}
                  style={{ width: '100%', padding: '6px 8px', textAlign: 'right' }} /></td>
                <td><input value={renglon.descripcion} onChange={(e) => actualizar(i, { descripcion: e.target.value })}
                  maxLength={300} style={{ width: '100%', padding: '6px 8px', fontSize: 12 }} /></td>
                <td>
                  {renglones.length > 2 && (
                    <button className="enlace" title="Eliminar renglón"
                      onClick={() => setRenglones((r) => r.filter((_, j) => j !== i))}>×</button>
                  )}
                </td>
              </tr>
            ))}
            <tr>
              <td style={{ fontWeight: 700 }}>Sumas · deben ser iguales (RN-01)</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(totalCargos)}</td>
              <td className="mono" style={{ textAlign: 'right', fontWeight: 700 }}>{dinero(totalAbonos)}</td>
              <td></td>
              <td></td>
            </tr>
          </tbody>
        </table>

        <p>
          <button className="enlace" onClick={() => setRenglones((r) => [...r, { ...RENGLON_VACIO }])}>
            + Agregar renglón
          </button>
        </p>

        {diferencia !== 0 && (
          <p className="error">
            <strong>Ajuste descuadrado: diferencia de ${dinero(Math.abs(diferencia))}.</strong>{' '}
            No se registran ajustes descuadrados (RN-01).
          </p>
        )}

        {error && <p className="error">{error}</p>}

        <div className="acciones">
          <button onClick={() => alCerrar()}>Descartar</button>
          <button className="primario" disabled={descuadrado || enviando || !concepto} onClick={() => void registrar()}>
            {enviando ? 'Registrando…' : 'Registrar ajuste'}
          </button>
        </div>
      </div>
    </div>
  )
}

/** Cancelación con motivo obligatorio: nada se borra, todo queda en el rastro (RF-406, C-06). */
function CancelacionDeAjuste({
  clienteId,
  anioDelEjercicio,
  ajuste,
  alCerrar,
}: {
  clienteId: string
  anioDelEjercicio: number
  ajuste: Poliza
  alCerrar: (aviso?: string) => void
}) {
  const [error, setError] = useState('')
  const [enviando, setEnviando] = useState(false)

  async function cancelar(evento: React.FormEvent<HTMLFormElement>) {
    evento.preventDefault()
    const motivo = String(new FormData(evento.currentTarget).get('motivo'))
    setError('')
    setEnviando(true)
    try {
      await api.cancelarAjusteDeCierre(clienteId, anioDelEjercicio, ajuste.id, motivo)
      alCerrar(`Ajuste ${ajuste.folioVisible} cancelado; se conserva íntegro con su motivo en la bitácora.`)
    } catch (e) {
      setError(e instanceof ErrorDeApi ? e.message : 'No se pudo cancelar el ajuste.')
      setEnviando(false)
    }
  }

  return (
    <div className="modal" {...telon(() => alCerrar())}>
      <form className="panel" onClick={(e) => e.stopPropagation()} onSubmit={cancelar}>
        <h2>Cancelar ajuste {ajuste.folioVisible}</h2>
        <label>
          Motivo de la cancelación (obligatorio)
          <input name="motivo" required maxLength={500} placeholder="Importe capturado en la cuenta equivocada" />
        </label>
        {error && <p className="error">{error}</p>}
        <div className="acciones">
          <button type="button" onClick={() => alCerrar()}>Volver</button>
          <button type="submit" className="primario" disabled={enviando}>
            {enviando ? 'Cancelando…' : 'Cancelar ajuste'}
          </button>
        </div>
      </form>
    </div>
  )
}
